import React from "react";
import { motion } from "framer-motion";
import { Zap, RotateCcw, Activity, Timer, MonitorCog, MessageCircle, Coins } from "lucide-react";
import VoteBanner from "../components/VoteBanner";
import AdminMap from "../components/AdminMap";
import WhatsAppSimulator from "../components/WhatsAppSimulator";
import StripeModal from "../components/StripeModal";
import PitchSection from "../components/PitchSection";
import CountUp from "../components/CountUp";
import { useSlotBooking } from "../hooks/useSlotBooking";

const PANEL_MOTION = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } };
const eur = (n) => "€" + Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

export default function SlotSaver() {
  const {
    providers, selectedId, setSelectedId,
    phase, mobileTab, setMobileTab,
    slot, matching, winningUser, receipt, bookedProviderId,
    secondsLeft, processingMs, recoveredToday,
    stripeOpen, setStripeOpen,
    isTriggering, isLocking, isPaying, busy,
    handleSimulate, handleBookNow, handlePay, handleReset,
  } = useSlotBooking();

  return (
    <div className="ss-noise relative min-h-screen bg-[#0a0a0a]">
      <VoteBanner />

      <div className="relative z-10">
        {/* Hero */}
        <header className="mx-auto max-w-7xl px-4 sm:px-6 pt-10 sm:pt-14 pb-6">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#25D366]">
            <span className="flex h-2 w-2 rounded-full bg-[#25D366] animate-pulse" />
            SlotSaver · Interactive Live Demo
          </div>

          <div
            data-testid="recovered-today-counter"
            className="mt-4 inline-flex items-center gap-2.5 rounded-full border border-[#FFCC00]/30 bg-[#FFCC00]/10 px-4 py-1.5"
          >
            <Coins size={15} className="text-[#FFCC00]" strokeWidth={2.5} />
            <span className="font-body text-sm text-zinc-300">
              Today we recovered{" "}
              <b className="font-mono text-[#FFCC00]" data-testid="recovered-today-value">
                <CountUp value={recoveredToday} format={eur} />
              </b>{" "}
              for sports centers
            </span>
          </div>
          <h1 className="mt-3 font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tighter text-white max-w-4xl">
            Last-minute cancellations?{" "}
            <span className="text-[#FF3B30]">We turn them into opportunities</span> you can book in
            30 seconds.
          </h1>
          <p className="mt-4 max-w-2xl font-body text-base sm:text-lg text-zinc-400">
            We recover free slots from padel, football and tennis courts, offer them at a discount
            to nearby users via WhatsApp, and handle payment with an anti-competition lock.
          </p>

          {/* live metrics strip */}
          <div className="mt-6 flex flex-wrap gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#121212] px-4 py-2.5" data-testid="metric-latency">
              <Timer size={16} className="text-[#FFCC00]" />
              <span className="font-mono text-sm text-zinc-300">
                Matching latency:{" "}
                <b className="text-white">{processingMs != null ? `${processingMs}ms` : "< 30s SLA"}</b>
              </span>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#121212] px-4 py-2.5" data-testid="metric-matched">
              <Activity size={16} className="text-[#007AFF]" />
              <span className="font-mono text-sm text-zinc-300">
                Matched users:{" "}
                <b className="text-white">{matching ? matching.total_matched : "—"}</b>
              </span>
            </div>
            <button
              onClick={handleReset}
              data-testid="reset-demo-button"
              className="ml-auto flex items-center gap-2 rounded-xl border border-white/10 bg-[#121212] px-4 py-2.5 font-mono text-sm text-zinc-300 transition-colors hover:border-white/30 hover:text-white"
            >
              <RotateCcw size={15} /> Reset Demo
            </button>
          </div>
        </header>

        {/* Mobile tab switcher (hidden on desktop) */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:hidden" data-testid="mobile-tabs">
          <div className="grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-[#121212] p-1.5">
            <button
              onClick={() => setMobileTab("admin")}
              data-testid="mobile-tab-admin"
              className={`flex items-center justify-center gap-2 rounded-xl py-3 font-head text-base font-bold uppercase tracking-wide transition-colors ${
                mobileTab === "admin" ? "bg-[#FF3B30] text-white" : "text-zinc-400"
              }`}
            >
              <MonitorCog size={18} /> Admin Control
            </button>
            <button
              onClick={() => setMobileTab("whatsapp")}
              data-testid="mobile-tab-whatsapp"
              className={`relative flex items-center justify-center gap-2 rounded-xl py-3 font-head text-base font-bold uppercase tracking-wide transition-colors ${
                mobileTab === "whatsapp" ? "bg-[#25D366] text-[#052e16]" : "text-zinc-400"
              }`}
            >
              <MessageCircle size={18} /> WhatsApp
              {mobileTab !== "whatsapp" && ["typing", "notified", "payment", "booked"].includes(phase) && (
                <span className="absolute right-3 top-2 flex h-2.5 w-2.5">
                  <span className="ss-ring absolute inline-flex h-full w-full rounded-full bg-[#25D366]" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#25D366]" />
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Two-column simulator */}
        <main className="mx-auto max-w-7xl px-4 sm:px-6 pb-8 pt-4 lg:pt-0">
          <div className="grid gap-6 lg:grid-cols-2">
            <motion.div
              {...PANEL_MOTION}
              transition={{ duration: 0.4 }}
              className={`${mobileTab === "admin" ? "block" : "hidden"} lg:block`}
            >
              <AdminMap
                providers={providers}
                selectedId={selectedId}
                onSelect={setSelectedId}
                activeSlot={slot}
                bookedProviderId={bookedProviderId}
                onSimulate={handleSimulate}
                isTriggering={isTriggering}
                disabled={busy}
              />
            </motion.div>
            <motion.div
              {...PANEL_MOTION}
              transition={{ duration: 0.4, delay: 0.1 }}
              className={`${mobileTab === "whatsapp" ? "block" : "hidden"} lg:block`}
            >
              <WhatsAppSimulator
                phase={phase}
                slot={slot}
                winningUser={winningUser}
                secondsLeft={secondsLeft}
                onBookNow={handleBookNow}
                isLocking={isLocking}
                receipt={receipt}
              />
            </motion.div>
          </div>
        </main>

        <PitchSection />

        <footer className="border-t border-white/10 py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 font-head text-lg font-bold uppercase tracking-tight text-white">
              <Zap size={18} className="text-[#FF3B30]" /> SlotSaver
            </div>
            <p className="font-mono text-xs text-zinc-600">
              Fabrizio Romano x Emergent Builder's Contest · Demo · Simulated payments
            </p>
          </div>
        </footer>
      </div>

      <StripeModal
        open={stripeOpen}
        onOpenChange={setStripeOpen}
        slot={slot}
        onPay={handlePay}
        isPaying={isPaying}
      />
    </div>
  );
}
